import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, RefreshCw } from 'lucide-react';
import {
  Button,
  Pagination,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@shared/ui';
import { flattenAccountingGroups, type FlatAccountingOperation } from '@entities/accounting';
import type { AccountingGroupDto } from '@shared/api/contracts';
import { isNegativeDecimal } from '@shared/lib/format-decimal';
import { formatAssetNetworkLabel } from '@shared/lib/asset-balance';
import { formatLedgerAmount, resolveAssetRemainder } from '@shared/lib/format-ledger-amount';
import { formatDateTime } from '@shared/lib/format-date';

interface AccountingTableProps {
  operations: AccountingGroupDto[];
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
  isLoading?: boolean;
  error?: unknown;
  onRefresh?: () => void;
  onPageChange?: (page: number) => void;
}

export function AccountingTable({
  operations,
  pagination,
  isLoading = false,
  error,
  onRefresh,
  onPageChange,
}: AccountingTableProps) {
  const { t } = useTranslation('cabinet');

  const flatOperations = useMemo(() => flattenAccountingGroups(operations), [operations]);

  return (
    <div className="surface-card">
      <div className="mb-3 flex items-center justify-between gap-3 sm:mb-4">
        <h2 className="section-title">{t('balance.history')}</h2>
        <Button
          variant="outline"
          size="icon-sm"
          className="rounded-full sm:size-10"
          onClick={onRefresh}
          loading={isLoading}
          leftIcon={<RefreshCw className="size-4" />}
          aria-label={t('common.retry', { ns: 'common' })}
        />
      </div>

      {error ? (
        <div className="flex flex-col items-center justify-center gap-3 py-10 text-center sm:py-12">
          <p className="font-sans text-sm text-destructive">{t('balance.loadError')}</p>
          <Button variant="outline" size="sm" onClick={onRefresh}>
            {t('common.retry', { ns: 'common' })}
          </Button>
        </div>
      ) : isLoading && !flatOperations.length ? (
        <div className="space-y-2 sm:space-y-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-9 w-full sm:h-10" />
          ))}
        </div>
      ) : !flatOperations.length ? (
        <div className="flex flex-col items-center justify-center gap-2 py-12 text-center sm:py-16">
          <FileText className="h-8 w-8 text-muted-foreground sm:h-10 sm:w-10" />
          <p className="font-sans text-sm font-medium text-foreground sm:text-base">{t('balance.empty')}</p>
          <p className="font-sans text-xs text-muted-foreground sm:text-sm">{t('balance.emptyDescription')}</p>
        </div>
      ) : (
        <>
          <div className="-mx-1 min-w-0 px-1">
            <Table className="table-compact">
              <TableHeader>
                <TableRow>
                  <TableHead>{t('balance.columns.asset')}</TableHead>
                  <TableHead className="text-right">{t('balance.columns.amount')}</TableHead>
                  <TableHead className="text-right">{t('balance.columns.balance')}</TableHead>
                  <TableHead>{t('balance.columns.date')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {flatOperations.map((operation: FlatAccountingOperation, index: number) => {
                  const isNegative = isNegativeDecimal(operation.sum);
                  const remainder = resolveAssetRemainder({
                    assetLabel: operation.assetLabel,
                    assetBalanceAmount: operation.assetBalanceAmount,
                    balanceAmount: operation.balance_amount,
                  });
                  const assetBadge = operation.assetLabel
                    ? operation.network
                      ? `${operation.assetLabel} · ${formatAssetNetworkLabel(
                          operation.network,
                          t('balance.historicalNetwork'),
                        )}`
                      : operation.assetLabel
                    : null;

                  return (
                    <TableRow
                      key={`${operation.createdAt}-${index}`}
                      className={operation.isFirstInGroup ? 'bg-[#f5fff3]' : undefined}
                    >
                      <TableCell className="text-muted-foreground">
                        {assetBadge ?? t('balance.legacyUsdt')}
                      </TableCell>
                      <TableCell
                        className={`text-right font-medium ${
                          isNegative ? 'text-fail-foreground' : 'text-success-foreground'
                        }`}
                      >
                        {formatLedgerAmount(operation.sum, {
                          assetLabel: operation.assetLabel,
                          signed: true,
                        })}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {remainder.amount == null
                          ? '—'
                          : formatLedgerAmount(remainder.amount, {
                              assetLabel: remainder.ticker,
                            })}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground">
                        {formatDateTime(operation.createdAt)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <Pagination
            page={pagination.page}
            limit={pagination.limit}
            total={pagination.total}
            onPageChange={(page) => onPageChange?.(page)}
          />
        </>
      )}
    </div>
  );
}
